import { useEffect, useState, useSyncExternalStore } from "react";
import { useStytchOrganization } from "./stytch-compat";
import { useTenantScope } from "../contexts/TenantScopeContext";
import { deleteCookie, getCookie, setCookie } from "../util/cookie";
import { PrimeQueryKeys } from "../shared/constants";

// Chrome maxes out at 400 days.
const MAX_COOKIE_AGE_DAYS = 399;

// `interfaces.ts` may not grow, so the admitted project's IFOA bit is declared here, beside
// its one reader.
declare module "../shared/interfaces" {
    interface TenantProject {
        /** The project organization's own `hasIfoa` (ai_engine `SessionProject.has_ifoa`), on
         *  the project a URL tab admitted. Null when the server could not read it. Absent from
         *  a descriptor that does not carry it: an older server, or an adopted project. */
        has_ifoa?: boolean | null;
    }
}

/**
 * Whether the organization that owns this tab's project uses IFOA, or null until anything has
 * loaded. Stytch's organization metadata owns the bit. A URL tab's credential can belong to
 * another organization (a staff member's own), so there the admitted project carries its own
 * organization's value; a `null` value means the server could not read it, and the tab shows
 * the default categories (no IFOA, Prime Contract) without reading another organization. A
 * legacy tab, and a descriptor without the field, read the session organization's metadata.
 */
function useProjectHasIfoa(): boolean | null {
    const scope = useTenantScope();
    const { organization } = useStytchOrganization();
    const project = scope.mode === "canonical" ? scope.selectedProject : null;
    if (project && project.has_ifoa !== undefined) return project.has_ifoa === true;
    return organization ? organization.trusted_metadata?.hasIfoa === "true" : null;
}

export function useHasIfoa(): boolean {
    return useProjectHasIfoa() === true;
}

// Per-user toggle (admin-menu checkbox, default off). The setter fires an event so an
// already-mounted FileViewer picks the change up without a reload — unlike the other
// cookie toggles, this one gates live overlay rendering.
const SEGMENTATION_OVERLAY_EVENT = "prime-segmentation-overlay-changed";

function drawingSegmentationOverlayEnabled(): boolean {
    return getCookie(PrimeQueryKeys.drawingSegmentationOverlayCookie) === "true";
}

export function setDrawingSegmentationOverlay(value: boolean) {
    setCookie(PrimeQueryKeys.drawingSegmentationOverlayCookie, String(value), MAX_COOKIE_AGE_DAYS);
    window.dispatchEvent(new Event(SEGMENTATION_OVERLAY_EVENT));
}

/** Whether to show the drawing-segmentation overlay (rooms/fixtures/level lines) in the file viewer. */
export function useDrawingSegmentationOverlay(): boolean {
    const [enabled, setEnabled] = useState(drawingSegmentationOverlayEnabled);
    useEffect(() => {
        const onChange = () => setEnabled(drawingSegmentationOverlayEnabled());
        window.addEventListener(SEGMENTATION_OVERLAY_EVENT, onChange);
        return () => window.removeEventListener(SEGMENTATION_OVERLAY_EVENT, onChange);
    }, []);
    return enabled;
}

// Browser-only admin diagnostic, default off. With the segmentation overlay on,
// this adds the check's exact raw/vector/fallback boxes and prevents the expensive
// geometry request entirely while disabled.
const FIXTURE_CLASH_GEOMETRY_EVENT = "prime-fixture-clash-geometry-changed";

function fixtureClashGeometryOverlayEnabled(): boolean {
    return getCookie(PrimeQueryKeys.fixtureClashGeometryCookie) === "true";
}

export function setFixtureClashGeometryOverlay(value: boolean) {
    setCookie(PrimeQueryKeys.fixtureClashGeometryCookie, String(value), MAX_COOKIE_AGE_DAYS);
    window.dispatchEvent(new Event(FIXTURE_CLASH_GEOMETRY_EVENT));
}

export function useFixtureClashGeometryOverlay(): boolean {
    const [enabled, setEnabled] = useState(fixtureClashGeometryOverlayEnabled);
    useEffect(() => {
        const onChange = () => setEnabled(fixtureClashGeometryOverlayEnabled());
        window.addEventListener(FIXTURE_CLASH_GEOMETRY_EVENT, onChange);
        return () => window.removeEventListener(FIXTURE_CLASH_GEOMETRY_EVENT, onChange);
    }, []);
    return enabled;
}

// Per-user toggle (admin-menu checkbox, default off). Same event pattern as the
// segmentation overlay: gates the admin-only entity-segmentation debug layer (the
// opening extractor's candidates drawn on the sheet).
const ENTITY_SEGMENTATION_EVENT = "prime-entity-segmentation-changed";

function drawingEntitySegmentationEnabled(): boolean {
    return getCookie(PrimeQueryKeys.drawingEntitySegmentationCookie) === "true";
}

export function setDrawingEntitySegmentation(value: boolean) {
    setCookie(PrimeQueryKeys.drawingEntitySegmentationCookie, String(value), MAX_COOKIE_AGE_DAYS);
    window.dispatchEvent(new Event(ENTITY_SEGMENTATION_EVENT));
}

/** Whether to show the entity-segmentation debug overlay (extractor candidates) in the file viewer. */
export function useDrawingEntitySegmentation(): boolean {
    const [enabled, setEnabled] = useState(drawingEntitySegmentationEnabled);
    useEffect(() => {
        const onChange = () => setEnabled(drawingEntitySegmentationEnabled());
        window.addEventListener(ENTITY_SEGMENTATION_EVENT, onChange);
        return () => window.removeEventListener(ENTITY_SEGMENTATION_EVENT, onChange);
    }, []);
    return enabled;
}

// Per-user toggle (admin-menu checkbox, default off). Inspect is everything the Drawing
// annotation tool knows — status bar, detector chips, suppression grades, hash ids, provenance
// marks, removed ghosts, the removal-reason prompt — for internal debugging. Annotate (the
// default) shows none of it: the annotator edits what is true on the sheet and the model
// feedback stays invisible (opening_annotation_ux_design.md §2). The old "ask why on remove"
// flag folded into this one; the chosen reason is still collected and discarded (C1, JOINT).
const DRAWING_ANNOTATION_INSPECT_EVENT = "prime-drawing-annotation-inspect-changed";

function drawingAnnotationInspectEnabled(): boolean {
    return getCookie(PrimeQueryKeys.drawingAnnotationInspectCookie) === "true";
}

export function setDrawingAnnotationInspect(value: boolean) {
    setCookie(PrimeQueryKeys.drawingAnnotationInspectCookie, String(value), MAX_COOKIE_AGE_DAYS);
    window.dispatchEvent(new Event(DRAWING_ANNOTATION_INSPECT_EVENT));
}

/** Whether the Drawing annotation layer shows its Inspect surfaces. */
export function useDrawingAnnotationInspect(): boolean {
    const [enabled, setEnabled] = useState(drawingAnnotationInspectEnabled);
    useEffect(() => {
        const onChange = () => setEnabled(drawingAnnotationInspectEnabled());
        window.addEventListener(DRAWING_ANNOTATION_INSPECT_EVENT, onChange);
        return () => window.removeEventListener(DRAWING_ANNOTATION_INSPECT_EVENT, onChange);
    }, []);
    return enabled;
}

// Per-user toggle (admin-menu checkbox, default off). Like the segmentation overlay, the
// setter fires an event so an already-mounted FileViewer picks up the change without a
// reload — this one gates the Shift-key "ask Marvin about a region" quasimode.
const ASK_REGION_EVENT = "prime-ask-region-changed";

function askRegionEnabled(): boolean {
    return getCookie(PrimeQueryKeys.askRegionCookie) === "true";
}

export function setAskRegionEnabled(value: boolean) {
    setCookie(PrimeQueryKeys.askRegionCookie, String(value), MAX_COOKIE_AGE_DAYS);
    window.dispatchEvent(new Event(ASK_REGION_EVENT));
}

/** Whether holding Shift in the file viewer arms the "ask Marvin about a region" quasimode. */
export function useAskRegionEnabled(): boolean {
    const [enabled, setEnabled] = useState(askRegionEnabled);
    useEffect(() => {
        const onChange = () => setEnabled(askRegionEnabled());
        window.addEventListener(ASK_REGION_EVENT, onChange);
        return () => window.removeEventListener(ASK_REGION_EVENT, onChange);
    }, []);
    return enabled;
}

// Per-user toggle (admin-menu checkbox, default off). Like the segmentation overlay, the
// setter fires an event so an already-mounted FileViewer picks up the change without a
// reload — this one gates the read-only system "Clash alerts" layer.
const CLASH_ALERT_LAYER_EVENT = "prime-clash-alert-layer-changed";

function clashAlertLayerEnabled(): boolean {
    return getCookie(PrimeQueryKeys.clashAlertLayerCookie) === "true";
}

export function setClashAlertLayer(value: boolean) {
    setCookie(PrimeQueryKeys.clashAlertLayerCookie, String(value), MAX_COOKIE_AGE_DAYS);
    window.dispatchEvent(new Event(CLASH_ALERT_LAYER_EVENT));
}

/** Whether to show the system "Clash alerts" layer in the file viewer. */
export function useClashAlertLayer(): boolean {
    const [enabled, setEnabled] = useState(clashAlertLayerEnabled);
    useEffect(() => {
        const onChange = () => setEnabled(clashAlertLayerEnabled());
        window.addEventListener(CLASH_ALERT_LAYER_EVENT, onChange);
        return () => window.removeEventListener(CLASH_ALERT_LAYER_EVENT, onChange);
    }, []);
    return enabled;
}

/**
 * Whether to surface the Prime Contract document type.
 *
 * Derived from `hasIfoa` (no dedicated org-config flag): Prime Contract is shown
 * to non-IFOA orgs, since the two types are mutually exclusive. It waits until
 * the value is known so Prime Contract doesn't briefly flash on for IFOA orgs
 * before their metadata arrives.
 */
export function useShowPrimeContract(): boolean {
    return useProjectHasIfoa() === false;
}

export function setShowDebugInfo(value: boolean) {
    setCookie(PrimeQueryKeys.showDebugInfoCookie, String(value), MAX_COOKIE_AGE_DAYS);
}

export function useShowDebugInfo(): boolean {
    return getCookie(PrimeQueryKeys.showDebugInfoCookie) === "true";
}

export function isDebugEnabled(): boolean {
    return getCookie(PrimeQueryKeys.showDebugInfoCookie) === "true";
}

export function setShowEmbeddingText(value: boolean) {
    setCookie(PrimeQueryKeys.showEmbeddingTextCookie, String(value), MAX_COOKIE_AGE_DAYS);
}

export function useShowEmbeddingText(): boolean {
    return getCookie(PrimeQueryKeys.showEmbeddingTextCookie) === "true";
}

export function setShowBuildingAssets(value: boolean) {
    setCookie(PrimeQueryKeys.buildingAssetsBrowserCookie, String(value), MAX_COOKIE_AGE_DAYS);
}

export function useShowBuildingAssets(): boolean {
    return getCookie(PrimeQueryKeys.buildingAssetsBrowserCookie) === "true";
}

export function setHasNoConversations(value: boolean) {
    setCookie(PrimeQueryKeys.emptyConversationHistoryCookie, String(value), MAX_COOKIE_AGE_DAYS);
}

export function hasNoConversations(): boolean {
    return getCookie(PrimeQueryKeys.emptyConversationHistoryCookie) === "true";
}

export function setEnableLocalCache(value: boolean) {
    setCookie(PrimeQueryKeys.enableLocalCacheCookie, String(value), MAX_COOKIE_AGE_DAYS);
}

export function useEnableLocalCache(): boolean {
    const cookieValue = getCookie(PrimeQueryKeys.enableLocalCacheCookie);
    // Default to true if cookie is not set
    return cookieValue === null || cookieValue === undefined || cookieValue === ""
        ? true
        : cookieValue === "true";
}

// Design v2 is a per-project flag (`design_v2`) with a personal browser override. This cookie
// is the override: "true" or "false" is this person's explicit choice and wins; absent, the
// project's flag decides (`flags/designV2Store.ts` resolves the two). The account menu's "Use
// the new design" and This browser write it.
const DESIGN_V2_EVENT = "prime-design-v2-changed";

/** This browser's explicit Design v2 choice, or null to follow the project. */
export function designV2Override(): boolean | null {
    const value = getCookie(PrimeQueryKeys.designV2BrowserCookie);
    return value === "true" ? true : value === "false" ? false : null;
}

export function subscribeDesignV2Override(listener: () => void): () => void {
    window.addEventListener(DESIGN_V2_EVENT, listener);
    // Cookies are shared with other tabs on this host; refresh when returning to one.
    window.addEventListener("focus", listener);
    return () => {
        window.removeEventListener(DESIGN_V2_EVENT, listener);
        window.removeEventListener("focus", listener);
    };
}

export function setDesignV2(value: boolean): void {
    setCookie(PrimeQueryKeys.designV2BrowserCookie, String(value), MAX_COOKIE_AGE_DAYS);
    window.dispatchEvent(new Event(DESIGN_V2_EVENT));
}

/** Drops this browser's choice, so the project's flag decides again. */
export function clearDesignV2Override(): void {
    deleteCookie(PrimeQueryKeys.designV2BrowserCookie);
    window.dispatchEvent(new Event(DESIGN_V2_EVENT));
}

export function useDesignV2Override(): boolean | null {
    return useSyncExternalStore(subscribeDesignV2Override, designV2Override, () => null);
}

// Per-browser opt-in asking the admin chat backend to run the Miranda engine.
// Read at send time (ui_context) and on panel open (warm); the server also
// selects Miranda when the tenant flag is on, regardless of this cookie.
const MIRANDA_ADMIN_CHAT_ENGINE_EVENT = "prime-miranda-admin-chat-engine-changed";

export function mirandaAdminChatEngineEnabled(): boolean {
    return getCookie(PrimeQueryKeys.mirandaAdminChatEngineCookie) === "true";
}

function subscribeMirandaAdminChatEngine(listener: () => void): () => void {
    window.addEventListener(MIRANDA_ADMIN_CHAT_ENGINE_EVENT, listener);
    window.addEventListener("focus", listener);
    return () => {
        window.removeEventListener(MIRANDA_ADMIN_CHAT_ENGINE_EVENT, listener);
        window.removeEventListener("focus", listener);
    };
}

export function setMirandaAdminChatEngine(value: boolean): void {
    setCookie(PrimeQueryKeys.mirandaAdminChatEngineCookie, String(value), MAX_COOKIE_AGE_DAYS);
    window.dispatchEvent(new Event(MIRANDA_ADMIN_CHAT_ENGINE_EVENT));
}

export function useMirandaAdminChatEngine(): boolean {
    return useSyncExternalStore(
        subscribeMirandaAdminChatEngine,
        mirandaAdminChatEngineEnabled,
        () => false,
    );
}
